import { useApolloClient } from '@apollo/client';
import { message, Space } from 'antd';
import { Formik } from 'formik';
import { get, pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ConfiguratorModuleSpecsFragment } from '../../../../api/fragments/ConfiguratorModuleSpecs';
import {
    UpdateConfiguratorModuleDocument,
    type UpdateConfiguratorModuleMutation,
    type UpdateConfiguratorModuleMutationVariables,
} from '../../../../api/mutations/updateConfiguratorModule';
import {
    ApplicationMarket,
    type ConfiguratorModuleUpdateSettings,
    EventLeadOriginType,
    EventMediumType,
} from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import { hasValue } from '../../../../utilities/fp';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { hasAppointmentScenario, hasPaymentScenario } from '../modules/implementations/shared';
import CounterSettingDetails from '../shared/CounterSettingDetails';
import { newZealandMarketValidator, singaporeMarketValidator } from '../shared/validators';
import { getFinancingApplicationMarket } from '../StandardApplicationModulePage/StandardApplicationModuleMainDetails';
import ConfiguratorModuleForm from './ConfiguratorModuleConfigurationForm';
import { type ConfiguratorModuleCounterKey } from './type';

const counterFields: ConfiguratorModuleCounterKey[] = [
    'applicationCounter',
    'reservationCounter',
    'leadCounter',
    'appointmentCounter',
    'insuranceCounter',
];

type ConfiguratorModuleConfigurationProps = {
    module: ConfiguratorModuleSpecsFragment;
};

export type ConfiguratorModuleConfigurationFormValues = ConfiguratorModuleUpdateSettings &
    Partial<Pick<ConfiguratorModuleSpecsFragment, ConfiguratorModuleCounterKey>>;

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.only(
        values => hasPaymentScenario(get('scenarios', values)),
        validators.compose(validators.requiredString('paymentSettingsId'), validators.requiredNumber('depositPrice'))
    ),
    validators.only(
        values => hasAppointmentScenario(values?.scenarios),
        validators.requiredString('appointmentModuleId')
    ),
    validators.only(values => hasValue('bankModuleId')(values), validators.requiredString('market')),
    validators.only(values => {
        const showInsuranceCalculator = get('showInsuranceCalculator', values);

        return showInsuranceCalculator;
    }, validators.requiredString('insuranceModuleId')),
    validators.only(values => {
        const showFinanceCalculator = get('showFinanceCalculator', values);

        return showFinanceCalculator;
    }, validators.requiredString('bankModuleId')),
    validators.only(values => {
        const market = get('market', values);
        const marketType = get('marketType', values);

        return market === ApplicationMarket.Singapore && !!marketType;
    }, singaporeMarketValidator),
    validators.only(values => {
        const market = get('market', values);
        const marketType = get('marketType', values);

        return market === ApplicationMarket.NewZealand && !!marketType;
    }, newZealandMarketValidator),
    validators.requiredArray('scenarios'),
    validators.requiredBoolean('tradeIn'),
    validators.requiredString('assignee.defaultId'),
    validators.requiredBoolean('showResetKYCButton'),
    validators.only(
        ({ capModuleId }) => capModuleId,
        validators.compose(
            validators.requiredBoolean('capPrequalification'),
            validators.requiredStringEnum('leadOrigin', EventLeadOriginType),
            validators.requiredStringEnum('leadMedium', EventMediumType),
            validators.only(
                ({ leadMedium }) => leadMedium !== EventMediumType.Walkin,
                validators.requiredString('leadCampaignId')
            )
        )
    )
);

const ConfiguratorModuleConfiguration = ({ module }: ConfiguratorModuleConfigurationProps) => {
    const { t } = useTranslation('configuratorModuleDetail');
    const apolloClient = useApolloClient();
    const validate = useValidator(validator);
    const initialValues: ConfiguratorModuleConfigurationFormValues = useMemo(
        () => ({
            ...pick(
                [
                    'displayName',
                    'testDrive',
                    'tradeIn',
                    'isTradeInAmountVisible',
                    'scenarios',
                    'assignee',
                    'depositPrice',
                    'paymentSettingsId',
                    'myInfoSettingId',
                    'liveChatSettingId',
                    'termsTitle',
                    'termsText',
                    'priceDisclaimer',
                    'promoCodeModuleId',
                    'isInventoryEnabled',
                    'market',
                    'showResetKYCButton',
                    'showFromValueOnVehicleDetails',
                    'showRemoteFlowButtonInKYCPage',
                    'skipForDeposit',
                    'bankDisplayPreference',
                    'externalUrl',
                    'displayAppointmentDatepicker',
                    'appointmentModuleId',
                    'insurerDisplayPreference',
                    'insurerIds',
                    'insuranceModuleId',
                    'financingPreference',
                    'isInsuranceOptional',
                    'showFinanceCalculator',
                    'showInsuranceCalculator',
                    'depositPrice',
                    'bankIds',
                    'capModuleId',
                    'isSearchCapCustomerOptional',
                    'capPrequalification',
                    'leadOrigin',
                    'leadMedium',
                    'leadCampaignId',
                    ...counterFields,
                ],
                module
            ),
            marketType: getFinancingApplicationMarket(module.marketType),
            bankModuleId: module?.bankModule?.id,
        }),
        [module]
    );

    const onSubmit = useHandleError(
        async (values: ConfiguratorModuleConfigurationFormValues) => {
            const { scenarios } = values;
            message.loading({
                content: t('configuratorModuleDetail:messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const settings: ConfiguratorModuleUpdateSettings = {
                ...pick(
                    [
                        'displayName',
                        'tradeIn',
                        'isTradeInAmountVisible',
                        'scenarios',
                        'assignee',
                        'myInfoSettingId',
                        'liveChatSettingId',
                        'termsTitle',
                        'termsText',
                        'priceDisclaimer',
                        'promoCodeModuleId',
                        'isInventoryEnabled',
                        'market',
                        'marketType',
                        'bankModuleId',
                        'showResetKYCButton',
                        'showFromValueOnVehicleDetails',
                        'showRemoteFlowButtonInKYCPage',
                        'skipForDeposit',
                        'bankDisplayPreference',
                        'externalUrl',
                        'displayAppointmentDatepicker',
                        'appointmentModuleId',
                        'insurerDisplayPreference',
                        'insurerIds',
                        'insuranceModuleId',
                        'financingPreference',
                        'isInsuranceOptional',
                        'showFinanceCalculator',
                        'showInsuranceCalculator',
                        'bankIds',
                        'capModuleId',
                        'isSearchCapCustomerOptional',
                        'capPrequalification',
                        'leadOrigin',
                        'leadMedium',
                        'leadCampaignId',
                    ],
                    values
                ),
                testDrive: hasAppointmentScenario(scenarios),
                paymentSettingsId: null,
                depositPrice: null,
                bankModuleId: values.showFinanceCalculator ? values.bankModuleId : null,
                capModuleId: values.capModuleId || null,
            };

            if (hasPaymentScenario(scenarios)) {
                // assign value if deposit
                settings.paymentSettingsId = values.paymentSettingsId;
                settings.depositPrice = values.depositPrice;
            }

            await apolloClient
                .mutate<UpdateConfiguratorModuleMutation, UpdateConfiguratorModuleMutationVariables>({
                    mutation: UpdateConfiguratorModuleDocument,
                    variables: { moduleId: module.id, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('configuratorModuleDetail:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateForm" name="updateForm" onSubmitCapture={handleSubmit}>
                    <Space direction="vertical">
                        <ConfiguratorModuleForm module={module} />
                        <CounterSettingDetails names={counterFields} />
                    </Space>
                </Form>
            )}
        </Formik>
    );
};

export default ConfiguratorModuleConfiguration;
